import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { SkillPill } from '../components/SkillPill';
import RatingBadge from '../components/RatingBadge';
import StarRating from '../components/StarRating';
import { DiscoverSkeleton } from '../components/SkeletonLoader';
import { 
  Search, TrendingUp, Star, Sparkles, Users, UserPlus, Check,
  ChevronRight, Flame, Clock, Target, Loader2, ArrowRight, X,
  User, Globe, Lock, Filter, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Discover = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  
  // States
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeFilter, setActiveFilter] = useState(searchParams.get('type') || 'all');
  const [searchResults, setSearchResults] = useState({ experts: [], users: [], communities: [] });
  const [isSearchMode, setIsSearchMode] = useState(!!searchParams.get('q'));
  const [searchLoading, setSearchLoading] = useState(false);
  
  const [data, setData] = useState({
    popular_experts: [],
    top_rated: [],
    new_experts: [],
    matching_experts: [],
    trending_domains: [],
    upcoming_events: []
  });
  const [followingStates, setFollowingStates] = useState({});
  // Sprint 1.0c V1.b — Item 9 (F2): search history
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadSearchHistory = async () => {
    try {
      const r = await axios.get(`${API}/discovery/search-history?limit=8`, { withCredentials: true });
      setSearchHistory(r.data.history || []);
    } catch { /* not logged in or no history */ }
  };

  const clearSearchHistory = async () => {
    try {
      await axios.delete(`${API}/discovery/search-history`, { withCredentials: true });
      setSearchHistory([]);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadSearchHistory();
  }, []);

  useEffect(() => {
    loadDiscoverData();
    
    const q = searchParams.get('q');
    if (q) {
      setSearchQuery(q);
      setIsSearchMode(true);
    }
  }, []);

  // Real-time search with debounce — Brief 27 oct D.1 : endpoint unifié
  // /search/all (experts + utilisateurs + communautés). Avant : /search/users
  // seulement, communautés toujours [] → onglet « Communautés » mort.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 1) {
        performSearch(searchQuery.trim(), activeFilter);
      } else {
        setSearchResults({ experts: [], users: [], communities: [] });
        setIsSearchMode(false);
        setSearchLoading(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDiscoverData = async () => {
    try {
      const [discoverRes, trendingRes] = await Promise.all([
        axios.get(`${API}/discover`, { withCredentials: true }),
        // Sprint 1.0c V1.a — item 8 (F1): live trending skills from real experts
        axios.get(`${API}/discovery/trending-skills?limit=12`).catch(() => ({ data: { trending: [] } })),
      ]);
      const liveTrending = (trendingRes.data?.trending || []).map(t => t.skill);
      setData({
        ...discoverRes.data,
        trending_domains: liveTrending.length > 0 ? liveTrending : discoverRes.data.trending_domains,
      });

      const states = {};
      [...discoverRes.data.popular_experts, ...discoverRes.data.top_rated,
       ...discoverRes.data.new_experts, ...discoverRes.data.matching_experts].forEach(expert => {
        states[expert.user_id] = expert.is_following;
      });
      setFollowingStates(states);
    } catch (error) {
      console.error('Error loading discover data:', error);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (query, type) => {
    if (!query || query.length < 1) {
      setSearchResults({ experts: [], users: [], communities: [] });
      setIsSearchMode(false);
      return;
    }
    
    setIsSearchMode(true);
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/search/all`, {
        params: { q: query },
        withCredentials: true
      });
      const d = response.data || {};
      setSearchResults({ experts: d.experts || [], users: d.users || [], communities: d.communities || [] });
      setFollowingStates((prev) => {
        const next = { ...prev };
        [...(d.experts || []), ...(d.users || [])].forEach((u) => { next[u.user_id] = !!u.is_following; });
        return next;
      });
    } catch (error) {
      console.error('Search error:', error);
      toast.error('La recherche a échoué. Réessaie dans un instant.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFollow = async (userId, e) => {
    e.stopPropagation();
    const isFollowing = followingStates[userId];
    
    try {
      if (isFollowing) {
        await axios.delete(`${API}/users/${userId}/follow`, { withCredentials: true });
      } else {
        await axios.post(`${API}/users/${userId}/follow`, {}, { withCredentials: true });
      }
      setFollowingStates(prev => ({ ...prev, [userId]: !isFollowing }));
      
      if (isSearchMode) {
        const patch = (list) => (list || []).map(u => u.user_id === userId ? { ...u, is_following: !isFollowing } : u);
        setSearchResults(prev => ({ ...prev, experts: patch(prev.experts), users: patch(prev.users) }));
      }
    } catch (error) {
      console.error('Error following/unfollowing:', error);
    }
  };

  // Brief 27 oct C.3 (RCA) — le bouton basculait `is_member` en optimiste même
  // quand le backend répondait `pending:true` (communauté sur invitation) :
  // affichage « Membre » mensonger, aucune demande visible. On lit la réponse.
  const handleJoinCommunity = async (communityId, isMember, e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(`${API}/communities/${communityId}/join`, {}, { withCredentials: true });
      setSearchResults(prev => ({
        ...prev,
        communities: (prev.communities || []).map(c => {
          if (c.community_id !== communityId) return c;
          if (data.pending) return { ...c, has_pending_request: true };
          return { ...c, is_member: !!data.is_member, member_count: data.member_count ?? c.member_count };
        })
      }));
      if (data.pending) toast.success("Demande envoyée — l'admin va l'examiner.");
      else if (data.is_member) toast.success('Tu as rejoint la communauté');
      else toast('Tu as quitté la communauté');
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'action n'a pas pu aboutir");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setIsSearchMode(true);
      setSearchParams({ q: searchQuery, type: activeFilter });
      performSearch(searchQuery, activeFilter);
    }
  };

  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter);
    if (searchQuery.length >= 2) {
      setSearchParams({ q: searchQuery, type: newFilter });
      performSearch(searchQuery, newFilter);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setSearchResults({ experts: [], users: [], communities: [] });
    setSearchParams({});
  };

  const handleDomainClick = (domain) => {
    setSearchQuery(domain);
    setIsSearchMode(true);
    setActiveFilter('users');
    setSearchParams({ q: domain, type: 'users' });
    performSearch(domain, 'users');
  };

  // Expert Card Component - Enhanced with shadows and borders
  const ExpertCard = ({ expert, showRating = false, badge = null }) => {
    const isFollowing = followingStates[expert.user_id];
    const isOwnProfile = currentUser?.user_id === expert.user_id;

    const handleBookClick = (e) => {
      e.stopPropagation();
      // Brief LOT1 — ouvre la modal de réservation sur le profil (flux unifié).
      navigate(`/app/profile/${expert.user_id}?book=1`);
    };

    return (
      <div
        onClick={() => navigate(`/app/profile/${expert.user_id}`)}
        className="group card-hover-lift bg-white dark:bg-gray-800 hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/30 dark:hover:to-pink-900/30 border-2 border-gray-100 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-700 rounded-2xl p-5 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-xl hover:shadow-purple-100/50 dark:hover:shadow-purple-900/30"
        data-testid={`expert-card-${expert.user_id}`}
      >
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 ring-4 ring-gray-50 dark:ring-gray-700 group-hover:ring-purple-100 dark:group-hover:ring-purple-800 transition-all shadow-lg">
            <AvatarImage src={expert.avatar} />
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold text-lg">
              {expert.display_name?.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-gray-900 dark:text-white text-lg">{expert.display_name}</h4>
              {badge && (
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${badge.className}`}>
                  {badge.text}
                </span>
              )}
            </div>
            
            {expert.bio && (
              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mt-1">{expert.bio}</p>
            )}
            
            {showRating && expert.rating_avg > 0 && (
              <div className="flex items-center gap-1 mt-2">
                <StarRating rating={expert.rating_avg} size="sm" />
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">({expert.rating_count} avis)</span>
              </div>
            )}

            {/* Brief B5 P1 bonus — mini-badge "Prochain créneau dans Xh" calculé
                depuis l'availability_schedule (champ next_available_slot du
                payload /api/discover). Affiché uniquement si le slot est dans
                les 7 jours pour rester pertinent. */}
            {expert.next_available_slot && (() => {
              const slotDate = new Date(expert.next_available_slot);
              const diffMs = slotDate.getTime() - Date.now();
              if (diffMs <= 0 || diffMs > 7 * 24 * 3600 * 1000) return null;
              const hours = Math.round(diffMs / 3600 / 1000);
              const days = Math.floor(hours / 24);
              const label = days >= 1
                ? `Prochain créneau dans ${days}j`
                : `Prochain créneau dans ${Math.max(1, hours)}h`;
              return (
                <span
                  className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-700/40"
                  data-testid={`expert-next-slot-${expert.user_id}`}
                  title={slotDate.toLocaleString('fr-FR')}
                >
                  <Calendar className="h-3 w-3" /> {label}
                </span>
              );
            })()}

            {expert.skills?.length > 0 && (
              <div className="flex gap-1.5 mt-3 flex-wrap" data-testid="discover-expert-card-skills">
                {expert.skills.slice(0, 3).map((skill, i) => (
                  <SkillPill key={i} testId={`discover-expert-card-skill-${i}`}>
                    {skill}
                  </SkillPill>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons column */}
          <div className="flex flex-col gap-2 shrink-0">
            {!isOwnProfile && (
              <>
                <Button
                  size="sm"
                  onClick={handleBookClick}
                  className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white hover:opacity-90 shadow-lg shadow-cyan-200 dark:shadow-cyan-900/30"
                  data-testid={`book-btn-${expert.user_id}`}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Réserver
                </Button>
                <Button
                  size="sm"
                  onClick={(e) => handleFollow(expert.user_id, e)}
                  className={`${
                    isFollowing 
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600' 
                      : 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-700'
                  }`}
                >
                  {isFollowing ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Suivi
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Suivre
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Brief Sep 2026 — carte "Session publique à venir" (section Découvrir)
  const UpcomingEventCard = ({ ev }) => {
    const start = new Date(ev.start_time);
    const dateLabel = start.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    const timeLabel = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return (
      <div
        onClick={() => navigate(`/event/${ev.event_id}`)}
        className="group bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 hover:border-teal-200 dark:hover:border-teal-700 rounded-2xl p-5 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-xl hover:shadow-teal-100/50 dark:hover:shadow-teal-900/30 flex flex-col gap-3"
        data-testid={`upcoming-event-card-${ev.event_id}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-700/40">
            <Calendar className="h-3 w-3" /> {dateLabel} · {timeLabel}
          </span>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${ev.is_free ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'}`}>
            {ev.is_free ? 'Gratuit' : `${ev.price} ${ev.currency || 'CHF'}`}
          </span>
        </div>
        <h4 className="font-bold text-gray-900 dark:text-white leading-snug line-clamp-2 group-hover:text-teal-700 dark:group-hover:text-teal-300 transition-colors">
          {ev.title}
        </h4>
        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="h-7 w-7 ring-2 ring-gray-50 dark:ring-gray-700">
              <AvatarImage src={ev.owner_avatar} />
              <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-xs font-bold">
                {ev.owner_name?.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{ev.owner_name}</span>
          </div>
          {ev.max_capacity > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
              <Users className="h-3.5 w-3.5" /> {ev.max_capacity} places
            </span>
          )}
        </div>
      </div>
    );
  };

  // Community Card for Search Results — Brief 27 oct C.3 / D.1
  const CommunityCard = ({ community }) => {
    const isPrivate = (community.join_mode || 'open') === 'invitation';
    const pending = !!community.has_pending_request;
    const canToggle = !community.is_creator && !pending;
    let label;
    let Icon = UserPlus;
    if (community.is_creator) { label = 'Ma communauté'; Icon = Check; }
    else if (community.is_member) { label = 'Membre'; Icon = Check; }
    else if (pending) { label = 'Demande envoyée'; Icon = Clock; }
    else if (isPrivate) { label = 'Demander à rejoindre'; }
    else { label = 'Rejoindre'; }
    return (
      <div
        onClick={() => navigate(`/app/community/${community.community_id}`)}
        className="group bg-white dark:bg-gray-800 hover:bg-gradient-to-br hover:from-green-50 hover:to-teal-50 dark:hover:from-green-900/30 dark:hover:to-teal-900/30 border-2 border-gray-100 dark:border-gray-700 hover:border-green-200 dark:hover:border-green-700 rounded-2xl p-5 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-xl hover:shadow-green-100/50 dark:hover:shadow-green-900/30"
        data-testid={`community-result-${community.community_id}`}
      >
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 ring-4 ring-gray-50 dark:ring-gray-700 group-hover:ring-green-100 dark:group-hover:ring-green-800 transition-all shadow-lg">
            <AvatarImage src={community.avatar_url} />
            <AvatarFallback className="bg-gradient-to-br from-green-500 to-teal-500 text-white font-bold text-lg">
              {community.name?.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-gray-900 dark:text-white text-lg">{community.name}</h4>
              {isPrivate ? (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"><Lock className="h-3 w-3" /> Privée</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><Globe className="h-3 w-3" /> Ouverte</span>
              )}
            </div>
            
            {community.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1 mt-1">{community.description}</p>
            )}
            
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <Users className="h-4 w-4" />
              <span className="font-medium">{community.member_count || 0} membre{(community.member_count || 0) > 1 ? 's' : ''}</span>
              {community.category && <span className="text-gray-300 dark:text-gray-600">·</span>}
              {community.category && <span>{community.category}</span>}
            </div>
          </div>

          <Button
            size="sm"
            disabled={!canToggle}
            onClick={(e) => canToggle ? handleJoinCommunity(community.community_id, community.is_member, e) : e.stopPropagation()}
            className={`shrink-0 ${
              community.is_member || community.is_creator || pending
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                : 'bg-gradient-to-r from-green-600 to-teal-600 text-white hover:opacity-90 shadow-lg shadow-green-200 dark:shadow-green-900/30'
            }`}
            data-testid={`community-join-${community.community_id}`}
          >
            <Icon className="h-4 w-4 mr-1" />
            {label}
          </Button>
        </div>
      </div>
    );
  };

  // Section Component with enhanced styling
  const Section = ({ title, icon: Icon, iconBgColor, iconColor, experts, badge, showRating }) => {
    if (experts.length === 0) return null;
    
    return (
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <div className={`p-2.5 rounded-xl ${iconBgColor} shadow-sm`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {experts.map((expert) => (
            <ExpertCard 
              key={expert.user_id} 
              expert={expert} 
              showRating={showRating}
              badge={badge}
            />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="discover-page">
      {/* Search Header - Enhanced */}
      <div className="sticky top-0 z-20 bg-gradient-to-b from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900 dark:to-transparent pb-4 pt-2 -mx-4 px-4">
        <form onSubmit={handleSearch} className="relative">
          <div className="relative flex items-center bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 focus-within:border-purple-500 rounded-2xl shadow-lg shadow-gray-100/50 dark:shadow-gray-900/50 transition-all">
            <Search className="absolute left-5 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowHistory(true)}
              onBlur={() => setTimeout(() => setShowHistory(false), 180)}
              placeholder="Rechercher un expert, une communauté, une compétence..."
              className="w-full pl-14 pr-36 py-4 bg-transparent border-0 text-gray-900 dark:text-white placeholder:text-gray-400 text-base focus:ring-0"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-32 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <Button 
              type="submit"
              className="absolute right-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 shadow-lg shadow-purple-200"
            >
              <Search className="h-4 w-4 mr-2" />
              Rechercher
            </Button>
          </div>

          {/* Sprint 1.0c V1.b — Item 9 (F2): search history dropdown */}
          {showHistory && !searchQuery.trim() && searchHistory.length > 0 && (
            <div
              className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-50 overflow-hidden"
              data-testid="search-history-dropdown"
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Recherches récentes
                </span>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); clearSearchHistory(); }}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                  data-testid="search-history-clear"
                >
                  Effacer
                </button>
              </div>
              <ul className="max-h-72 overflow-y-auto">
                {searchHistory.map((h) => (
                  <li key={h.query + (h.searched_at || '')}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSearchQuery(h.query);
                        setSearchParams({ q: h.query });
                        performSearch(h.query, activeFilter);
                        setShowHistory(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 dark:hover:bg-gray-700 text-left"
                      data-testid={`search-history-item-${h.query}`}
                    >
                      <Search className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-700 dark:text-gray-200 truncate flex-1">{h.query}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>

        {/* Filter Tabs - Only show when in search mode — Brief 27 oct D.1 : 3 groupes */}
        {isSearchMode && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1" data-testid="search-filter-tabs">
            {[
              { key: 'all', label: 'Tout', icon: Filter, count: (searchResults.experts?.length || 0) + (searchResults.users?.length || 0) + (searchResults.communities?.length || 0) },
              { key: 'experts', label: 'Experts', icon: Sparkles, count: searchResults.experts?.length || 0 },
              { key: 'users', label: 'Utilisateurs', icon: User, count: searchResults.users?.length || 0 },
              { key: 'communities', label: 'Communautés', icon: Users, count: searchResults.communities?.length || 0 }
            ].map(({ key, label, icon: Icon, count }) => (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                data-testid={`search-filter-${key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border-2 whitespace-nowrap ${
                  activeFilter === key
                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 shadow-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
                {!searchLoading && <span className="text-xs opacity-70">({count})</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search Results */}
      {isSearchMode ? (
        <div className="space-y-8 mt-6" data-testid="search-results">
          {searchLoading ? (
            <DiscoverSkeleton />
          ) : (
            <>
              {/* Experts Results */}
              {(activeFilter === 'all' || activeFilter === 'experts') && searchResults.experts?.length > 0 && (
                <div data-testid="search-group-experts">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40">
                      <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    Experts ({searchResults.experts.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {searchResults.experts.map((user) => (
                      <ExpertCard key={user.user_id} expert={user} showRating={true} />
                    ))}
                  </div>
                </div>
              )}

              {/* Users Results */}
              {(activeFilter === 'all' || activeFilter === 'users') && searchResults.users?.length > 0 && (
                <div data-testid="search-group-users">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
                      <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    Utilisateurs ({searchResults.users.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {searchResults.users.map((user) => (
                      <ExpertCard key={user.user_id} expert={user} showRating={false} />
                    ))}
                  </div>
                </div>
              )}

              {/* Communities Results */}
              {(activeFilter === 'all' || activeFilter === 'communities') && searchResults.communities?.length > 0 && (
                <div data-testid="search-group-communities">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/40">
                      <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    Communautés ({searchResults.communities.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {searchResults.communities.map((community) => (
                      <CommunityCard key={community.community_id} community={community} />
                    ))}
                  </div>
                </div>
              )}

              {/* No Results (global or for the active group) */}
              {(() => {
                const groups = { experts: searchResults.experts?.length || 0, users: searchResults.users?.length || 0, communities: searchResults.communities?.length || 0 };
                const visible = activeFilter === 'all' ? groups.experts + groups.users + groups.communities : (groups[activeFilter] || 0);
                if (visible > 0) return null;
                const labels = { experts: 'expert', users: 'utilisateur', communities: 'communauté' };
                return (
                  <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/60 rounded-2xl" data-testid="search-empty">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      <Search className="h-10 w-10 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {activeFilter === 'all' ? 'Aucun résultat' : `Aucun ${labels[activeFilter]} trouvé`}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">
                      Rien pour « <span className="font-medium">{searchQuery}</span> »{activeFilter !== 'all' && (groups.experts + groups.users + groups.communities) > 0 ? ' dans cette catégorie — essaie « Tout ».' : '. Essaie un autre mot-clé, un nom ou une compétence.'}
                    </p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {activeFilter !== 'all' && (groups.experts + groups.users + groups.communities) > 0 && (
                        <Button onClick={() => handleFilterChange('all')} className="bg-purple-600 hover:bg-purple-700 text-white">Voir tout</Button>
                      )}
                      <Button variant="outline" onClick={clearSearch} className="border-gray-300 dark:border-gray-600">
                        Retour à la découverte
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      ) : (
        /* Discovery Content */
        <>
          {/* Hero Title */}
          <div className="text-center mb-8 mt-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
              Découvrez des{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                experts
              </span>
            </h1>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-lg">
              Trouvez des mentors, apprenez de nouvelles compétences et connectez-vous avec des professionnels
            </p>
          </div>

          {/* Trending Domains — Sprint 1.0c V1.a item 8 (F1): live from real experts */}
          {data.trending_domains.length > 0 && (
            <div className="mb-10 p-6 bg-gradient-to-br from-gray-50 to-purple-50/50 dark:from-gray-800 dark:to-purple-900/20 rounded-2xl border border-gray-100 dark:border-gray-700" data-testid="discover-trending-domains">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-purple-500" />
                Domaines en tendance
              </h3>
              <div className="flex flex-wrap gap-2">
                {data.trending_domains.map((domain, i) => (
                  <button
                    key={i}
                    onClick={() => handleDomainClick(domain)}
                    className="px-4 py-2 rounded-full bg-white dark:bg-gray-700 hover:bg-purple-100 dark:hover:bg-purple-900/40 border-2 border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600 text-gray-700 dark:text-gray-200 hover:text-purple-700 dark:hover:text-purple-300 transition-all text-sm font-medium shadow-sm hover:shadow"
                    data-testid={`domain-${domain}`}
                  >
                    {domain}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Matching Experts */}
          {data.matching_experts.length > 0 && (
            <Section
              title="Recommandés pour vous"
              icon={Target}
              iconBgColor="bg-cyan-100"
              iconColor="text-cyan-600"
              experts={data.matching_experts}
              badge={{ text: "Match", className: "bg-cyan-100 text-cyan-700 border border-cyan-200" }}
              showRating={true}
            />
          )}

          {/* Popular Experts */}
          <Section
            title="Experts populaires"
            icon={Flame}
            iconBgColor="bg-orange-100"
            iconColor="text-orange-600"
            experts={data.popular_experts}
            badge={{ text: "Populaire", className: "bg-orange-100 text-orange-700 border border-orange-200" }}
            showRating={false}
          />

          {/* Brief Sep 2026 — Sessions publiques à venir */}
          {data.upcoming_events?.length > 0 && (
            <div className="mb-10" data-testid="discover-upcoming-events">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sessions publiques à venir</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.upcoming_events.map((ev) => (
                  <UpcomingEventCard key={ev.event_id} ev={ev} />
                ))}
              </div>
            </div>
          )}

          {/* Top Rated */}
          {data.top_rated.length > 0 && (
            <Section
              title="Les mieux notés"
              icon={Star}
              iconBgColor="bg-yellow-100"
              iconColor="text-yellow-600"
              experts={data.top_rated}
              showRating={true}
            />
          )}

          {/* New Experts */}
          <Section
            title="Nouveaux talents"
            icon={Sparkles}
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
            experts={data.new_experts}
            badge={{ text: "Nouveau", className: "bg-purple-100 text-purple-700 border border-purple-200" }}
            showRating={false}
          />

          {/* AI Assistant CTA */}
          <div className="mt-12 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 rounded-2xl p-8 text-center shadow-xl shadow-purple-200">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              Vous ne savez pas par où commencer ?
            </h3>
            <p className="text-white/80 mb-6 max-w-md mx-auto">
              Notre assistant IA peut vous aider à trouver l'expert idéal en fonction de vos besoins
            </p>
            <Button
              onClick={() => navigate('/app/assistant')}
              className="bg-white text-purple-600 hover:bg-gray-100 shadow-lg"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Demander à l'assistant
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Discover;
